import {
    BlackjackGameResult,
    BlackjackGameState,
    SpeedrunGameResult, SpeedrunGameState, ExecuteActionRequest,
    GameState,
    Inventory, RouletteGameResult,
    RouletteGameState, Time
} from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";

/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    public async getRouletteStatus(color: string): Promise<RouletteGameResult> {
        return this.getJsonApi<RouletteGameResult>(`game/roulette-status?color=${color}`);
    }

    public async getRouletteState(): Promise<RouletteGameState> {
        return this.getJsonApi<RouletteGameState>("game/roulette");
    }

    public async getSpeedrunState(): Promise<SpeedrunGameState> {
        return this.getJsonApi<SpeedrunGameState>("game/speedrun-state");
    }

    /**
     * Retrieves the player's blackjack result to check if they lost.
     */
    public async getBlackjackStatusLose(): Promise<BlackjackGameResult> {
        return this.getJsonApi<BlackjackGameResult>("game/blackjack-lost");
    }

    /**
     * Retrieves the player's blackjack result to check if they won.
     */
    public async getBlackjackStatusWin(): Promise<BlackjackGameResult> {
        return this.getJsonApi<BlackjackGameResult>("game/blackjack-win");
    }

    /**
     * Get the state if the player is playing blackjack
     */
    public async getBlackjackState(): Promise<BlackjackGameState> {
        return this.getJsonApi<BlackjackGameState>("game/blackjack");
    }

    /**
     * Get the inventory of the current player from the server application
     *
     * @returns The inventory of the current player
     */
    public async getInventory(): Promise<Inventory> {
        const inventory: Inventory = await this.getJsonApi<Inventory>("game/inventory");
        return inventory;
    }

    /**
     * Retrieves the current time from the server application
     *
     * @returns An object containing the raw and formatted time
     */
    public async getCurrentTime(): Promise<Time> {
        return this.getJsonApi<Time>("game/time");
    }

    /**
     * Get the speedrun result of the current player from the server application
     *
     * @returns The speedrun result of the current player
     */
    public async getSpeedrunResult(): Promise<SpeedrunGameResult> {
        return this.getJsonApi<SpeedrunGameResult>("game/speedrun");
    }

    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }
}
